import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { createCheckoutSession } from '../lib/stripe';

// Types
export type PlanType = 'free' | 'starter' | 'pro';

interface UserFlags {
  plan_override: PlanType | null;
  is_beta: boolean;
}

interface Plan {
  type: PlanType;
  name: string;
  price: number;
  dailyMinutes: number;
  features: string[];
}

interface SubscriptionContextType {
  currentPlan: PlanType;
  plans: Record<PlanType, Plan>;
  remainingMinutes: number;
  loading: boolean;
  userFlags: UserFlags | null;
  upgradePlan: (plan: PlanType) => Promise<void>;
  useMinutes: (minutes: number) => void;
}

// Create context with default values
const SubscriptionContext = createContext<SubscriptionContextType>({
  currentPlan: 'free',
  plans: {
    free: { type: 'free', name: 'Free', price: 0, dailyMinutes: 10, features: [] },
    starter: { type: 'starter', name: 'Starter', price: 0, dailyMinutes: 0, features: [] },
    pro: { type: 'pro', name: 'Pro', price: 0, dailyMinutes: 0, features: [] },
  },
  remainingMinutes: 0,
  loading: false,
  userFlags: null,
  upgradePlan: async () => {},
  useMinutes: () => {},
});

// Custom hook to use the subscription context
export const useSubscription = () => useContext(SubscriptionContext);

// Provider component
export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState<PlanType>('free');
  const [userFlags, setUserFlags] = useState<UserFlags | null>(null);
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [loading, setLoading] = useState(false);

  // Define plans
  const plans: Record<PlanType, Plan> = {
    free: {
      type: 'free',
      name: 'Free',
      price: 0,
      dailyMinutes: 10,
      features: [
        '10 minutes per day',
        'Basic voice and vision capabilities',
        'Access to community support',
      ],
    },
    starter: {
      type: 'starter',
      name: 'Starter',
      price: 9,
      dailyMinutes: 60,
      features: [
        '60 minutes per day',
        'HD voice and vision quality',
        'Priority support',
        'Save up to 5 session recordings',
      ],
    },
    pro: {
      type: 'pro',
      name: 'Pro',
      price: 29,
      dailyMinutes: 300,
      features: [
        '300 minutes per day',
        'Premium voice and vision quality',
        'Priority support',
        'Unlimited session recordings',
        'Advanced AI capabilities',
        'Custom voice settings',
      ],
    },
  };

  // Fetch user flags and subscription data
  useEffect(() => {
    if (!user) return;

    const fetchUserFlags = async () => {
      // Ensure user records exist first (with error handling)
      try {
        await supabase.rpc('ensure_user_records_exist', {
          p_user_id: user.id
        });
      } catch (error) {
        console.warn('Error ensuring user records for flags:', error);
        // Continue anyway - the query might still work
      }
      
      const { data, error } = await supabase
        .from('user_flags')
        .select('plan_override, is_beta')
        .eq('user_id', user.id)
        .single();
      
      if (!error && data) {
        setUserFlags(data);
        if (data.plan_override) {
          setCurrentPlan(data.plan_override);
        }
      }
    };

    const fetchSubscription = async () => {
      // Ensure user records exist first (with error handling)
      try {
        await supabase.rpc('ensure_user_records_exist', {
          p_user_id: user.id
        });
      } catch (error) {
        console.warn('Error ensuring user records for subscription:', error);
        // Continue anyway - the query might still work
      }
      
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('plan_type, status')
        .eq('user_id', user.id)
        .single();
      
      if (!error && data && data.status === 'active') {
        setCurrentPlan(data.plan_type);
      }
    };
    
    fetchUserFlags();
    fetchSubscription();
  }, [user]);

  // Load user subscription data from localStorage (production only)
  useEffect(() => {
    if (user) {
      let storedPlan: PlanType = 'free';
      
      // If no override, check Stripe subscription
      if (!userFlags?.plan_override) {
        const savedPlan = localStorage.getItem(`voicevision_plan_${user.id}`);
        if (savedPlan && ['free', 'starter', 'pro'].includes(savedPlan)) {
          storedPlan = savedPlan as PlanType;
        }
        setCurrentPlan(storedPlan);
        localStorage.setItem(`voicevision_plan_${user.id}`, storedPlan);
      }

      // Get remaining minutes or reset if it's a new day
      const lastReset = localStorage.getItem(`voicevision_lastReset_${user.id}`);
      const today = new Date().toDateString();
      
      if (lastReset !== today) {
        // Reset daily minutes
        const planMinutes = plans[storedPlan]?.dailyMinutes || plans.free.dailyMinutes;
        setRemainingMinutes(planMinutes);
        localStorage.setItem(`voicevision_minutes_${user.id}`, planMinutes.toString());
        localStorage.setItem(`voicevision_lastReset_${user.id}`, today);
      } else {
        // Use stored remaining minutes
        const storedMinutes = localStorage.getItem(`voicevision_minutes_${user.id}`);
        setRemainingMinutes(storedMinutes ? parseInt(storedMinutes, 10) : plans[storedPlan].dailyMinutes);
      }
    } else {
      // Reset state when user logs out
      setCurrentPlan('free');
      setRemainingMinutes(0);
    }
  }, [user, userFlags]);

  // Change plan function
  const upgradePlan = async (plan: PlanType) => {
    try {
      console.log('SubscriptionContext: Upgrading to plan:', plan);
      console.log('SubscriptionContext: Current user:', user?.id);
      console.log('SubscriptionContext: User flags:', userFlags);
      console.log('SubscriptionContext: Environment mode:', import.meta.env.VITE_MODE);
      
      if (!user) return;
      
      // Handle complimentary access
      if (userFlags?.plan_override) {
        const proceed = confirm(`You have complimentary ${userFlags.plan_override} access. Test the ${plan} upgrade flow anyway?`);
        if (!proceed) return;
      }
      
      // Check if Stripe is properly configured
      if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY.includes('placeholder')) {
        alert('Stripe not configured for this environment.');
        return;
      }
      
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Please sign in to upgrade your plan');
        return;
      }
      
      setLoading(true);
      
      // Start Stripe checkout
      console.log('Starting Stripe checkout for plan:', plan);
      console.log('SubscriptionContext: About to call createCheckoutSession');
      await createCheckoutSession(plan);
      
    } catch (error) {
      console.error('Error upgrading plan:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('SubscriptionContext: Full upgrade error:', errorMessage);
      alert(`Failed to start checkout: ${errorMessage}. Please try again or contact support.`);
    } finally {
      setLoading(false);
    }
  };

  // Use minutes function
  const useMinutes = (minutes: number) => {
    if (!user) return;
    
    const newRemaining = Math.max(0, remainingMinutes - minutes);
    setRemainingMinutes(newRemaining);
    localStorage.setItem(`voicevision_minutes_${user.id}`, newRemaining.toString());
  };

  const value = {
    currentPlan,
    plans,
    userFlags,
    remainingMinutes,
    loading,
    upgradePlan,
    useMinutes,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};